import React from "react";

const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:)/i;

const createLink = (label, url, key) => {
  let href = url;

  // If no protocol, try to add https:// for known domain-like patterns
  if (!SAFE_URL_PATTERN.test(url)) {
    if (/^www\./i.test(url) || /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(url)) {
      href = `https://${url}`;
    } else {
      // Not a valid URL – return as plain text
      return label;
    }
  }

  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="as-text-blue"
    >
      {label}
    </a>
  );
};

/**
 * Converts supported links within ticket text into clickable React elements.
 *
 * Supports:
 * - Markdown links: [Student Portal](https://student.myaie.ac/...)
 * - HTTP and HTTPS URLs
 * - URLs beginning with www.
 * - Domain URLs without a protocol
 * - Mailto links
 *
 * @param {string} value - The ticket text containing links.
 * @returns {Array<string | React.ReactElement> | null}
 * An array containing text and link elements, or null when the value is empty
 * or is not a string.
 */
export const renderTicketLinks = (value) => {
  // Reject non‑string values (instead of coercing them).
  if (typeof value !== "string" || !value) {
    return null;
  }

  const pattern =
    /(\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\))|(https?:\/\/[^\s)]+|mailto:[^\s)]+|www\.[^\s)]+|\b(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s)]*)?)/gi;

  const result = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      result.push(value.slice(lastIndex, match.index));
    }

    const markdownLabel = match[2];
    const markdownUrl = match[3];
    const plainUrl = match[4];

    if (markdownUrl) {
      result.push(
        createLink(markdownLabel, markdownUrl, `ticket-link-${match.index}`)
      );
    } else if (plainUrl) {
      result.push(
        createLink(plainUrl, plainUrl, `ticket-link-${match.index}`)
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < value.length) {
    result.push(value.slice(lastIndex));
  }

  return result;
};

/**
 * Optimized component that memoizes the rendered output
 * Use this component instead of calling renderTicketLinks directly
 */
export const RenderTicketLinksMemoized = React.memo(({ value }) => {
  const renderedContent = renderTicketLinks(value);

  if (!renderedContent) {
    return null;
  }

  return <>{renderedContent}</>;
});